import { BookmarkIcon, CircleCheckIcon, HomeIcon, StarsIcon, TrendingIcon } from "@signet/ui";
import { defineWebModule, lazyPage } from "@signet/web-kit";
import { lazy } from "react";
import "./staff.css";

/* ──────────────────────────────────────────────────────────────────────────
   Ideas' web half. For staff, the Inspiration page (what creators do with
   their videos and why they pass on some). For creators, Home, Viral
   Vault, Saved for later and Completed in their portal (the frame is
   Creators'), and their niches on their login on People and in the hero
   of their page on Creators. Portal code loads only when a creator opens it.
   ────────────────────────────────────────────────────────────────────────── */

const HomePage = lazyPage(() => import("./home.tsx"), "HomePage");
const VaultPage = lazyPage(() => import("./vault.tsx"), "VaultPage");
const SavedPage = lazyPage(() => import("./lists.tsx"), "SavedPage");
const CompletedPage = lazyPage(() => import("./lists.tsx"), "CompletedPage");
const InspirationPage = lazyPage(() => import("./inspiration.tsx"), "InspirationPage");
const StaffNiches = lazy(() => import("./staff-niches.tsx").then((m) => ({ default: m.StaffNiches })));
const HeroNiches = lazy(() => import("./hero-niches.tsx").then((m) => ({ default: m.HeroNiches })));
const DoneArt = lazy(() => import("@signet/web-kit/portal").then((m) => ({ default: m.DoneArt })));

const TAILORED = { "ideas.tab": "tailored" };
const SAVED = { "ideas.tab": "saved" };

export default defineWebModule({
  id: "ideas",
  // Staff: how creators use their portal, and why they pass on videos (in AI Team, beside Viral Vault).
  nav: { label: "Inspiration", icon: StarsIcon, to: "/inspiration", needs: "ideas.feedback:view" },
  routes: [{ path: "/inspiration", component: InspirationPage, needs: "ideas.feedback:view" }],
  // A creator's niches in the hero of their page on Creators, beside the facts about them.
  creatorHeroRows: [{ id: "ideas.niches", needs: "ideas.creator:view", order: 10, component: HeroNiches }],
  commands: [
    { id: "ideas.inspiration", label: "Inspiration", to: "/inspiration", needs: "ideas.feedback:view", hint: "Creators' portal activity", keywords: "feedback not interested saved completed dismissed blocked portal creators review" },
  ],
  portal: {
    for: "creators",
    pages: [
      { path: "", label: "Home", icon: HomeIcon, order: 0, component: HomePage },
      // Staff's Viral Vault, read only. A tab on Home until Jay put it in the sidebar (2026-10-27).
      { path: "vault", label: "Viral Vault", short: "Vault", icon: TrendingIcon, order: 5, component: VaultPage },
      { path: "saved", label: "Saved for later", short: "Saved", icon: BookmarkIcon, order: 10, component: SavedPage },
      { path: "completed", label: "Completed videos", short: "Done", icon: CircleCheckIcon, order: 20, component: CompletedPage },
    ],
    staffSections: [{ id: "ideas.niches", label: "Niches", needs: "ideas.creator:view", order: 10, component: StaffNiches }],
    /*
     * The welcome tour through Home and Viral Vault, after Creators' photo and colours (Jay and the boss,
     * 2026-10-29). In the boss's order: save a video, see it in Saved, tick it off in the real player and
     * follow it to Completed, then Breakouts. Where there is a place to tap (the menu, My accounts), the
     * step shows where and tapping it moves on.
     */
    tour: [
      { id: "ideas.hero", order: 30, page: "", target: "hero", title: "Your banner", body: <>The big picture on Home is yours too. Tap <strong>Change photo</strong> on it to use any picture you like.</> },
      { id: "ideas.foryou", order: 40, page: "", set: TAILORED, target: "tab-tailored", title: "For you", body: "Viral videos picked for your niches. New ones come in every day, so check back often." },
      { id: "ideas.niches", order: 50, page: "", set: TAILORED, target: "niches", title: "Your niches", body: <>These decide what shows up in For you. Tap <strong>Add niches</strong> to add or remove any of them, as often as you like.</> },
      {
        id: "ideas.video",
        order: 60,
        page: "",
        set: TAILORED,
        target: ["first-card", "tab-tailored"],
        pad: 10,
        tryIt: '[data-tour="first-card"] .ideas-bookmark',
        tap: '[data-tour="first-card"] .ideas-bookmark',
        title: "Found one you like?",
        body: <>Tap a video to watch it. Tap <strong>Save</strong> to keep it for later, or the <strong>x</strong> if it doesn't fit you and it's gone.</>,
      },
      { id: "ideas.saved", order: 70, page: "", set: SAVED, target: "tab-saved", title: "Saved", body: "Everything you save lands here, so it's ready when you sit down to film." },
      {
        id: "ideas.done",
        order: 80,
        page: "",
        // Home opens their first saved video in the player; with none saved, the card shows the tap instead.
        set: { ...SAVED, "ideas.player": "first-saved" },
        target: "done-button",
        pad: 6,
        tap: '[data-tour="done-button"]',
        tryIt: '[data-tour="done-button"]',
        advance: "after",
        art: DoneArt,
        title: "Filmed it? Tick it off",
        body: <>When you've made it, open the video and tap <strong>I've done it</strong>.</>,
      },
      {
        id: "ideas.done-nav",
        order: 90,
        page: "",
        set: SAVED,
        target: "nav-completed",
        tap: '[data-tour="nav-completed"]',
        tryIt: '[data-tour="nav-completed"]',
        advance: true,
        title: "It moves to Completed",
        body: <>Every video you tick off goes to <strong>Completed videos</strong>. Tap it to have a look.</>,
      },
      { id: "ideas.completed", order: 100, page: "completed", target: ["done-first", "completed-head"], pad: 10, title: "Completed videos", body: "Everything you've made, in one place. The newest is always first." },
      { id: "ideas.breakouts", order: 110, page: "", set: { "ideas.tab": "breakouts" }, target: "tab-breakouts", title: "Breakouts", body: "The biggest videos of the week across every niche, not just yours. New ones are added all week." },
      {
        id: "ideas.vault-nav",
        order: 120,
        target: "nav-vault",
        tap: '[data-tour="nav-vault"]',
        tryIt: '[data-tour="nav-vault"]',
        advance: true,
        title: "Viral Vault",
        body: <>Tap <strong>Viral Vault</strong> in the menu to see every viral video we track.</>,
      },
      { id: "ideas.vault", order: 130, page: "vault", set: { "ideas.vault": "all" }, target: "vault-filters", title: "Every viral video", body: "Every viral video we track, across every niche. Filter by niche, views or how recently it went up." },
      {
        id: "ideas.mine",
        order: 140,
        page: "vault",
        set: { "ideas.vault": "all" },
        target: "vault-mine",
        pad: 6,
        tap: '[data-tour="vault-mine"]',
        tryIt: '[data-tour="vault-mine"]',
        advance: true,
        title: "My accounts",
        body: <>Tap <strong>My accounts</strong> to see your own Instagram accounts and how your videos did on them.</>,
      },
      {
        id: "ideas.best",
        order: 150,
        page: "vault",
        set: { "ideas.vault": "mine" },
        target: ["mine-first", "mine-head"],
        pad: 10,
        title: "Your best videos",
        body: "Each account's top videos, ranked. They already worked for your followers, so they're the best ones to make again.",
      },
    ],
  },
});
